var di = require('core/di');
var GlobalStore = di.get('stores/global');

/**
 * Env wrapper around DOM-related globals
 * @module
 */

/**
 * @return the documentElement
 */
exports.getDocumentElement = function () {
  return document.documentElement;
};

/**
 * @return {string} - Contents of document.cookie
 */
exports.getCookieString = function(): string {
  return document.cookie || '';
};

/**
 * Set a cookie (with value/options) in the format expected by document.cookie
 * @param {string} cookieString
 */
exports.setCookie = function(cookieString:string) {
  document.cookie = cookieString;
};

exports.querySelector = function(selector) {
  if(__EXPERIMENTAL_SHADOW_DOM__) {
    return require('env/shadow_dom').querySelector(selector);
  }
  return document.querySelector(selector);
};

exports.querySelectorAll = function(selector) {
  if(__EXPERIMENTAL_SHADOW_DOM__) {
    return require('env/shadow_dom').querySelectorAll(selector);
  }
  return document.querySelectorAll(selector);
};

/**
 * @param {string} URI to parse
 * @returns {HTMLHyperlinkElementUtils}
 */
exports.parseUri = function parseUri(uri:string): HTMLHyperlinkElementUtils {
  var e = exports.createElement('a');
  e.href = uri;
  return e;
};

/**
 * @param {HTMLElement} root
 * @returns {Array.<HTMLElement>}
 *        References to all elements in the tree rooted at the given root, sans the root.
 */
exports.childrenOf = function(root:HTMLElement): HTMLElement[] {
  // ES5-compatible coercion of a NodeList to an Array
  if(__EXPERIMENTAL_SHADOW_DOM__) {
    return Array.prototype.slice.call(require('env/shadow_dom').querySelectorAll('*', root));
  }
  return Array.prototype.slice.call(root.querySelectorAll('*'));
};

exports.createElement = function(nodeName) {
  return document.createElement(nodeName);
};

/**
 * @returns {boolean}
 *        If all of the elements in the received HTML are or were at one point in the DOM.
 *        (They may have been removed by other JavaScript that has already run.)
 */
exports.isReady = function(): boolean {
  return GlobalStore.domContentLoadedHasFired()
    || document.readyState === 'interactive'
    || document.readyState === 'complete';
};

/**
 * Function to check if page is loaded, analagous to PageLoad
 * @return {boolean}
 */
exports.isLoaded = function(): boolean {
  return document.readyState === 'complete';
};

/**
 * Adds an event handler to document ready and returns the unlisten fn
 *
 * @param {EventListener} handler
 * @return {Function} unlisten function
 */
exports.addReadyHandler = function(handler:EventListener): Function {
  // TODO use the EventStream and bind a single handler for DOMContentLoaded instead of binding multiple
  // TODO IE8 support
  document.addEventListener('DOMContentLoaded', handler);

  return function() {
    exports.removeReadyHandler(handler);
  };
};

exports.removeReadyHandler = function(handler:EventListener) {
  // TODO IE8 support
  return function() {
    document.removeEventListener('DOMContentLoaded', handler);
  };
};

/**
 * @returns {string}
 */
exports.getReferrer = function(): string {
  return document.referrer;
};

/**
 * Get [readyState](https://developer.mozilla.org/en-US/docs/Web/API/Document/readyState) property of the document.
 * @returns {string} "loading" or "complete" according to the [spec](https://html.spec.whatwg.org/multipage/dom.html#current-document-readiness)
 */
exports.getReadyState = function(): string {
  return document.readyState;
};

/**
 * Warning: this function is dangerous! In particular, if the document is already loaded (`getReadyState` is not "loading"),
 * then the written fragment will OVERWRITE all existing content. Also, it might fail (silently) in XHTML pages.
 *
 * Make sure you know what you are doing!
 *
 * More detail: https://developer.mozilla.org/en-US/docs/Web/API/Document/write
 *
 * Also, CSP prevents this from working to add `script` tags unless "unsafe-inline" is allowed.
 *
 * @param {string} htmlFragment Fragment of HTML to write to the document.
 */
exports.write = function(htmlFragment:string) {
  if (!safeToAttemptDocumentWrite()) {
    throw new Error('Aborting attempt to write to already-loaded document');
  }
  document.write(htmlFragment);
};

exports.appendToHead = function(node) {
  return exports.appendTo(document.head, node);
};

exports.appendTo = function(parentNode, node) {
  parentNode.appendChild(node);
};

/**
 * Wrapper around document.addEventListener, that returns an unlisten function
 * @param {string} type the event type
 * @param {EventListener} handler the event handler function
 * @param {boolean} useCapture
 * @return {Function} a remove listener function
 */
exports.addEventListener = function(type:string, handler:EventListener, useCapture:boolean): Function {
  document.addEventListener(type, handler, useCapture);

  return function() {
    document.removeEventListener(type, handler, useCapture);
  };
};

/**
 * Get the current script via [document.currentScript](https://developer.mozilla.org/en-US/docs/Web/API/Document/currentScript) (if supported)
 */
exports.getCurrentScript = function() {
  if (document.currentScript) {
    return document.currentScript;
  }
  // TODO(tyler): fallback?
};

/**
 * Get the parent Element of a given node.
 * For browsers which don't implement Node.prototype.parentElement for all node types.
 * @param {Node} node
 * @returns {Element}
 */
exports.parentElement = function(node:Node): Element {
  var result = node.parentNode;
  while (result.nodeType !== Node.ELEMENT_NODE) {
    result = result.parentNode;
  }

  return result as Element;
};

/**
 * Check to see if the document can be safely written to using document.write()
 *
 * See here for overview of document.readyState
 * http://www.whatwg.org/specs/web-apps/current-work/multipage/dom.html#current-document-readiness
 *
 * What happens when state is changed from "loading" to interactive:
 * http://www.whatwg.org/specs/web-apps/current-work/multipage/the-end.html#stop-parsing
 *
 * @return {boolean}
 */
function safeToAttemptDocumentWrite(): boolean {
  return exports.getReadyState() === 'loading';
}
